/* td-research-library — filtering, live search and scroll-spy for the
   Evidence Library on /pages/research. Vanilla, no dependencies, and safe to
   run more than once (each root is initialised at most once) so it survives
   theme-editor section re-renders. */
(function () {
  'use strict';

  var INIT = 'rlInitialised';

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function init(root) {
    if (root.dataset[INIT] === 'true') return;
    root.dataset[INIT] = 'true';

    var rail = root.querySelector('[data-rl-rail]');
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-rl-filter]'));
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-rl-group]'));
    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-rl-row]'));
    var status = root.querySelector('[data-rl-status]');
    var empty = root.querySelector('[data-rl-empty]');
    var searchWrap = root.querySelector('[data-rl-search]');
    var input = root.querySelector('[data-rl-input]');
    var clearBtn = root.querySelector('[data-rl-clear]');
    var resetBtn = root.querySelector('[data-rl-reset]');

    if (!rows.length) return;

    var total = rows.length;
    var activeCat = 'all';

    // Cache each row's plain title once so search highlighting can rebuild it
    // from scratch every keystroke instead of marking already-marked markup.
    rows.forEach(function (row) {
      var textEl = row.querySelector('.rl-row__text');
      row._rlTextEl = textEl;
      row._rlTitle = textEl ? textEl.textContent : '';
      row._rlHaystack = (row.getAttribute('data-rl-text') || '').toLowerCase();
      row._rlCat = row.getAttribute('data-rl-cat') || '';
    });

    function highlight(row, query) {
      var el = row._rlTextEl;
      if (!el) return;
      var title = row._rlTitle;

      // Track marked state explicitly: once <mark> elements are in place the
      // node's textContent still equals the plain title, so comparing strings
      // would never detect that the highlights need clearing.
      function unmark() {
        if (row._rlMarked) {
          el.textContent = title;
          row._rlMarked = false;
        }
      }

      if (!query) {
        unmark();
        return;
      }
      var re = new RegExp('(' + escapeRegExp(query) + ')', 'ig');
      if (!re.test(title)) {
        unmark();
        return;
      }
      re.lastIndex = 0;
      el.textContent = '';
      var last = 0;
      var match;
      while ((match = re.exec(title)) !== null) {
        if (match.index > last) {
          el.appendChild(document.createTextNode(title.slice(last, match.index)));
        }
        var mark = document.createElement('mark');
        mark.textContent = match[0];
        el.appendChild(mark);
        last = match.index + match[0].length;
        if (re.lastIndex === match.index) re.lastIndex++; // guard against zero-length
      }
      if (last < title.length) {
        el.appendChild(document.createTextNode(title.slice(last)));
      }
      row._rlMarked = true;
    }

    function apply(options) {
      var opts = options || {};
      var query = input ? input.value.trim().toLowerCase() : '';
      var shown = 0;
      var perCat = {};

      rows.forEach(function (row) {
        var matchesCat = activeCat === 'all' || row._rlCat === activeCat;
        var matchesText = !query || row._rlHaystack.indexOf(query) !== -1;
        var visible = matchesCat && matchesText;

        row.hidden = !visible;
        if (visible) shown++;

        // Rail counts should reflect the search, not the category filter —
        // otherwise every other category would read 0 while one is selected.
        if (!query || matchesText) {
          perCat[row._rlCat] = (perCat[row._rlCat] || 0) + 1;
        }
        highlight(row, query);
      });

      groups.forEach(function (group) {
        var id = group.getAttribute('data-rl-group');
        var visibleRows = group.querySelectorAll('[data-rl-row]:not([hidden])').length;
        group.hidden = visibleRows === 0;

        var countEl = group.querySelector('[data-rl-group-count]');
        if (countEl) {
          var totalForGroup = countEl.getAttribute('data-rl-group-total');
          countEl.textContent = query && visibleRows !== Number(totalForGroup)
            ? visibleRows + ' of ' + totalForGroup
            : totalForGroup;
        }

        if (opts.animate) {
          var rowWrap = group.querySelector('.rl-rows');
          if (rowWrap) {
            rowWrap.classList.remove('is-refiltering');
            void rowWrap.offsetWidth; // restart the CSS animation
            rowWrap.classList.add('is-refiltering');
          }
        }
      });

      buttons.forEach(function (btn) {
        var target = btn.getAttribute('data-rl-filter');
        var count = target === 'all'
          ? Object.keys(perCat).reduce(function (sum, k) { return sum + perCat[k]; }, 0)
          : (perCat[target] || 0);
        var countEl = btn.querySelector('.rl-rail__count');
        if (countEl) countEl.textContent = count;
        btn.classList.toggle('is-empty', count === 0 && target !== 'all');
        btn.setAttribute('aria-current', target === activeCat ? 'true' : 'false');
      });

      if (status) {
        status.innerHTML = 'Showing <b>' + shown + '</b> of ' + total + ' studies';
      }
      if (empty) {
        empty.setAttribute('data-visible', shown === 0 ? 'true' : 'false');
      }
      if (searchWrap) {
        searchWrap.classList.toggle('is-filled', query.length > 0);
      }
    }

    if (rail) {
      rail.addEventListener('click', function (event) {
        var btn = event.target.closest('[data-rl-filter]');
        if (!btn || !rail.contains(btn)) return;
        activeCat = btn.getAttribute('data-rl-filter');
        apply({ animate: true });

        // Keep the list in view when filtering from the mobile chip scroller.
        if (window.matchMedia('(max-width: 1000px)').matches) {
          var main = root.querySelector('.rl-main');
          if (main && main.getBoundingClientRect().top < 0) {
            main.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      });
    }

    if (input) {
      // Searching is global. Without this, a category selected earlier silently
      // scopes the query and a term that matches elsewhere reads as "no results".
      input.addEventListener('input', debounce(function () {
        if (input.value.trim() && activeCat !== 'all') activeCat = 'all';
        apply();
      }, 130));
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && input.value) {
          event.preventDefault();
          input.value = '';
          apply();
        }
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        if (input) {
          input.value = '';
          input.focus();
        }
        apply();
      });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        if (input) input.value = '';
        activeCat = 'all';
        apply({ animate: true });
      });
    }

    // Scroll-spy: only meaningful while "All" is selected, since any other
    // filter leaves a single group on the page.
    if ('IntersectionObserver' in window && groups.length > 1) {
      var spy = new IntersectionObserver(
        function (entries) {
          if (activeCat !== 'all') return;
          entries.forEach(function (entry) {
            var id = entry.target.getAttribute('data-rl-group');
            var btn = root.querySelector('[data-rl-filter="' + id + '"]');
            if (btn) btn.classList.toggle('is-inview', entry.isIntersecting);
          });
        },
        { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
      );
      groups.forEach(function (group) { spy.observe(group); });
    }

    apply();
  }

  function boot() {
    document.querySelectorAll('[data-rl-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', boot);
})();
